/**
 * Convert 24-hour time format to 12-hour format with AM/PM
 * @param time24 - Time in 24-hour format (e.g., "14:30")
 * @returns Time in 12-hour format (e.g., "2:30 PM")
 */
export function formatTimeTo12Hour(time24: string): string {
  if (!time24) return '';
  
  const [hourStr, minuteStr] = time24.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  
  const period = hour >= 12 ? 'PM' : 'AM';
  
  // Convert to 12-hour format
  if (hour === 0) {
    hour = 12; // Midnight
  } else if (hour > 12) {
    hour = hour - 12;
  }
  
  // Format minute with leading zero if needed
  const minuteFormatted = minute.toString().padStart(2, '0');
  
  return `${hour}:${minuteFormatted} ${period}`;
}

/**
 * Format a date object to a readable string
 * @param date - Date object
 * @returns Formatted date string (e.g., "Monday, October 21, 2025")
 */
export function formatDateLong(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a date object to a short date string
 * @param date - Date object
 * @returns Formatted date string (e.g., "Oct 21, 2025")
 */
export function formatDateShort(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get time string from date object in 24-hour format (local timezone)
 * @param date - Date object
 * @returns Time string (e.g., "14:30")
 */
export function getTimeString(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Get time string from date object in 12-hour format with AM/PM (local timezone)
 * @param date - Date object
 * @returns Time string (e.g., "2:30 PM")
 */
export function formatTimeTo12HourFromDate(date: Date): string {
  return formatTimeTo12Hour(getTimeString(date));
}

/**
 * Format date and time together (local timezone for time)
 * @param date - Date object
 * @returns Formatted string (e.g., "Monday, October 21, 2025 at 2:30 PM")
 */
export function formatDateTimeLong(date: Date): string {
  return `${formatDateLong(date)} at ${formatTimeTo12HourFromDate(date)}`;
}

/**
 * Format date and time in a compact format (local timezone)
 * @param date - Date object
 * @returns Formatted string using locale settings
 */
export function formatDateTimeShort(date: Date): string {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

